import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import type {
  DataResponse,
  NahladObnovy,
  ObnovaStav,
  ObnovitVysledok,
  SuborZalohy,
  ZakazkaZalohy,
} from "@/api/schemas";

/** `ok` = zálohuje sa; ostatné znamenajú, že sa treba pozrieť na server. */
export type StavZaloh = "ok" | "zastarane" | "chyba" | "nebezi";

export interface ZalohyStav {
  stav: StavZaloh;
  posledna_zaloha: string | null;
  vek_hodin: number | null;
  velkost_b: number;
  pocet: number;
  posledne_overenie: string | null;
  overenie_ok: boolean;
  chyba: string | null;
  adresar: string;
}

/**
 * Stav zálohovania databázy.
 *
 * Appka zálohy nespúšťa — číta len `stav.json`, ktorý píše kontajner
 * `backup`. Preto je to jediný `useQuery` bez mutácií.
 *
 * **Bez `refetchInterval`**: stav sa mení raz denne, minútový polling by
 * bol zbytočná záťaž. Na dotiahnutie čerstvého stavu stačí prekliknúť
 * tab alebo obnoviť stránku.
 */
export function useZalohyStav(
  enabled = true,
): UseQueryResult<DataResponse<ZalohyStav>> {
  return useQuery({
    queryKey: queryKeys.zalohy.stav(),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<ZalohyStav>>("/zalohy/stav");
      return data;
    },
    enabled,
    staleTime: 60_000,
  });
}

// ─── Obnova zákazky zo zálohy (KOLO 198) ─────────────────────
//
// Zálohu do dočasnej databázy obnoví kontajner `backup` — appka len
// zapíše požiadavku a pozerá jej stav. Zákazky a náhľad čítajú
// z pripravenej zálohy, obnova pridá variantu do živej DB.

/** Kým kontajner zálohu nepripravil, stav sa pýta každé 3 s. */
export function bezi(stav: ObnovaStav | null | undefined): boolean {
  return stav?.stav === "caka" || stav?.stav === "pripravuje";
}

export function useSuboryZaloh(): UseQueryResult<DataResponse<SuborZalohy[]>> {
  return useQuery({
    queryKey: queryKeys.zalohy.subory(),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<SuborZalohy[]>>(
        "/zalohy/subory",
      );
      return data;
    },
    staleTime: 60_000,
  });
}

/**
 * Posledná požiadavka na prípravu zálohy (`null` = žiadna).
 *
 * Polling **len kým sa pripravuje** — pripravená záloha sa už nemení
 * a zbytočné dotazy by len zaťažovali server.
 */
export function useObnovaStav(): UseQueryResult<DataResponse<ObnovaStav | null>> {
  return useQuery({
    queryKey: queryKeys.zalohy.obnova(),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<ObnovaStav | null>>(
        "/zalohy/obnova",
      );
      return data;
    },
    refetchInterval: (q) => (bezi(q.state.data?.data) ? 3_000 : false),
  });
}

export function usePripravZalohu(): UseMutationResult<
  DataResponse<ObnovaStav>,
  Error,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (subor: string) => {
      const { data } = await api.post<DataResponse<ObnovaStav>>(
        "/zalohy/obnova",
        { subor },
      );
      return data;
    },
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.zalohy.obnova(), data);
    },
  });
}

/** „Hotovo“ — dočasnú databázu zmaže kontajner. */
export function useUkonciObnovu(): UseMutationResult<void, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.delete("/zalohy/obnova");
    },
    onSuccess: () => {
      // Hneď späť na výber zálohy — invalidácia celého `obnova()` by ešte
      // raz pýtala zákazky a náhľad zo zálohy, ktorá už končí (409)
      qc.setQueryData(queryKeys.zalohy.obnova(), { data: null });
      void qc.invalidateQueries({
        queryKey: queryKeys.zalohy.obnova(),
        exact: true,
      });
    },
  });
}

export function useZakazkyZalohy(
  pripravaId: number | null,
  hladat: string,
): UseQueryResult<DataResponse<ZakazkaZalohy[]>> {
  return useQuery({
    queryKey: queryKeys.zalohy.zakazky(pripravaId ?? 0, hladat),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<ZakazkaZalohy[]>>(
        "/zalohy/obnova/zakazky",
        { params: hladat ? { hladat } : undefined },
      );
      return data;
    },
    enabled: pripravaId !== null,
    placeholderData: (pred) => pred,
  });
}

export function useNahladObnovy(
  pripravaId: number | null,
  zakazkaId: string | null,
): UseQueryResult<DataResponse<NahladObnovy>> {
  return useQuery({
    queryKey: queryKeys.zalohy.nahlad(pripravaId ?? 0, zakazkaId ?? ""),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<NahladObnovy>>(
        `/zalohy/obnova/zakazky/${zakazkaId}`,
      );
      return data;
    },
    enabled: pripravaId !== null && zakazkaId !== null,
  });
}

export function useObnovZoZalohy(): UseMutationResult<
  DataResponse<ObnovitVysledok>,
  Error,
  { zakazkaId: string; variantaIds: string[] }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ zakazkaId, variantaIds }) => {
      const { data } = await api.post<DataResponse<ObnovitVysledok>>(
        `/zalohy/obnova/zakazky/${zakazkaId}/obnovit`,
        { varianta_ids: variantaIds },
      );
      return data;
    },
    onSuccess: () => {
      // Zákazka má novú variantu (alebo je späť) — náhľad, zoznam
      // zákaziek zálohy aj zákazky v appke treba načítať znova
      void qc.invalidateQueries({ queryKey: queryKeys.zalohy.obnova() });
      void qc.invalidateQueries({ queryKey: queryKeys.zakazky.all });
      void qc.invalidateQueries({ queryKey: queryKeys.audit.all });
    },
  });
}
